import { useEffect, useRef } from 'react';

/**
 * Wireframe sheet 10, frame K1 — "Dialog closes → Returns to the control that
 * opened it, never to the top of the page."
 *
 * Fluent v9 does provide this, but only through `DialogTrigger`: that is what
 * stamps the tabster `restorer: Target` attribute onto the element that opened
 * the surface. Every dialog in this app is opened from state instead (a state
 * flag drives `open`), so nothing carries that attribute and focus lands on
 * `<body>` once the surface unmounts — measured, not assumed. This hook
 * restores it explicitly, which also makes the behaviour assertable in jsdom
 * rather than depending on tabster's DOM walking.
 *
 * The opener is captured from `focusin` while the dialog is closed rather than
 * from the click handler, because the control that opens a dialog is not always
 * the control focus should come back to: `Dashboard`'s delete dialog is opened
 * from a `MenuItem` that unmounts with its menu, and Fluent then returns focus
 * to the menu's trigger button — which is the element the user can actually see
 * afterwards, and the one this hook ends up remembering.
 */
export function useRestoreFocusOnClose(open: boolean): void {
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open) return undefined;
    function remember(event: FocusEvent) {
      if (event.target instanceof HTMLElement) openerRef.current = event.target;
    }
    document.addEventListener('focusin', remember);
    return () => document.removeEventListener('focusin', remember);
  }, [open]);

  useEffect(() => {
    if (!open && wasOpen.current) {
      const opener = openerRef.current;
      // A detached opener (a menu item that closed with its menu) cannot be
      // focused; better to leave focus where it is than to throw.
      if (opener?.isConnected) opener.focus();
    }
    wasOpen.current = open;
  }, [open]);
}
